import React from 'react'
import { CalendarRange } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCount, formatMoney } from '@/lib/format'
import { paceBand } from '@/lib/plain-language'
import { DeltaChip, percentFrom, pointsFrom, type Delta } from '@/components/dashboard/Delta'
import { HelpTooltip } from '@/components/HelpTooltip'
import { useFigureScope } from '@/lib/figure-scope'
import type { PerformanceMetrics } from '@/types/calendar'

/**
 * The month glance strip above the price calendar — what a revenue manager reads
 * for the DISPLAYED month, said like a hotelier, not an analyst.
 *
 * Laid out as a single COMPACT inline band (no header bar, no stacked tiles) so the
 * calendar stays above the fold: a teal-anchored title block on the left, then the
 * metrics flowing to its right, each a tidy three-line micro-tile
 * (label · value+delta · one caption).
 *
 * Every figure is canonical: the same on-the-books path the dashboard's
 * MonthOnTheBooks card uses, so occupancy / revenue / ADR here MATCH the dashboard.
 *
 * DISPLAY ONLY — zero arithmetic on money, rates or deltas (CLAUDE.md Data Integrity).
 * `occupancyDeltaPts`, `revenueDeltaPct` and `adr` arrive already-computed from the
 * backend; the helpers below format them, never recompute them. Every vs-last-year
 * comparison is guarded on a non-null delta — when STLY has no real data the
 * comparison chip is omitted entirely, never rendered as an empty "vs last year".
 */

// Worded pace band → text tone. Positive/negative carry the semantic colour so the
// operator reads "ahead / behind" at a glance; neutral stays foreground.
const PACE_TEXT_TONE: Record<'positive' | 'neutral' | 'negative', string> = {
  positive: 'text-emerald-700',
  neutral: 'text-foreground',
  negative: 'text-rose-700',
}

interface Metric {
  label: string
  value: string
  valueClass?: string
  /** Optional comparison chip rendered inline beside the value. */
  delta?: Delta | null
  /** One tight sub-line under the value — volume, comparison or plain description. */
  caption?: React.ReactNode
}

/**
 * One metric = at most three short lines (label · value[+chip] · caption), so every
 * tile is the same height and the band never grows past the hero figure. Numbers are
 * backend-computed; we only group digits and colour deltas, never derive a value.
 */
function Tile({ metric }: { metric: Metric }) {
  return (
    <div className="min-w-[92px]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{metric.label}</p>
      <div className="mt-1 flex items-center gap-1.5">
        <span className={cn('text-lg font-semibold leading-none tabular-nums text-foreground', metric.valueClass)}>
          {metric.value}
        </span>
        {metric.delta && <DeltaChip delta={metric.delta} />}
      </div>
      {metric.caption && (
        <p className="mt-1 text-[11px] leading-tight text-muted-foreground tabular-nums">{metric.caption}</p>
      )}
    </div>
  )
}

interface PerformanceBannerProps {
  metrics: PerformanceMetrics
}

export default function PerformanceBanner({ metrics }: PerformanceBannerProps) {
  const cur = metrics.currency
  // "Building ADR" at a combined inventory (server label), "ADR" everywhere else.
  const { adrLabel } = useFigureScope()

  // vs-last-year chips — null when STLY carries no real data, which omits the chip.
  const occDelta = pointsFrom(metrics.occupancyDeltaPts)
  const revDelta = percentFrom(metrics.revenueDeltaPct)

  const pace = metrics.avgPace != null ? paceBand(metrics.avgPace) : null

  // The room-night volume the occupancy % hides, plus the forward "still to sell" lever.
  // Backend-computed integers (formatCount groups digits only — never derives them).
  const occScale = (
    <>
      {formatCount(metrics.nightsSold)} of {formatCount(metrics.nightsAvailable)} room-nights
      {metrics.nightsToSell != null && (
        <span className="text-foreground/70">
          {' · '}
          {formatCount(metrics.nightsToSell)} still to sell
        </span>
      )}
    </>
  )

  const metricTiles: Metric[] = [
    {
      label: 'Occupancy',
      value: `${metrics.occupancyPct}%`,
      delta: occDelta,
      caption: occScale,
    },
    {
      label: 'Revenue on the books',
      value: formatMoney(metrics.revenue, cur),
      delta: revDelta,
      caption: metrics.revenueStly != null ? `vs ${formatMoney(metrics.revenueStly, cur)} last year` : undefined,
    },
    // ADR is realized revenue ÷ room-nights sold — omit the tile when nothing sold.
    ...(metrics.adr != null
      ? [
          {
            label: adrLabel ?? 'ADR',
            value: formatMoney(metrics.adr, cur),
            caption: 'the rate you earned',
          } satisfies Metric,
        ]
      : []),
    // Booking pace stays worded (docs/revenue-vocabulary.md § Plain-language) — never a multiplier.
    ...(pace
      ? [
          {
            label: 'Booking pace',
            value: pace.label,
            valueClass: PACE_TEXT_TONE[pace.tone],
            caption: 'vs a typical year',
          } satisfies Metric,
        ]
      : []),
    // Manual overrides — a plain "N days set by hand", hidden when there are none.
    ...(metrics.overrideDays > 0
      ? [
          {
            label: 'Set by hand',
            value: `${metrics.overrideDays} ${metrics.overrideDays === 1 ? 'day' : 'days'}`,
            caption: `of ${metrics.totalDays} this month`,
          } satisfies Metric,
        ]
      : []),
  ]

  const title = metrics.isCurrent ? 'This month on the books' : `${metrics.monthLabel} ${metrics.year}`
  const subtitle = metrics.isCurrent ? `Confirmed · ${metrics.monthLabel} ${metrics.year}` : 'Confirmed bookings'

  return (
    <section className="overflow-hidden rounded-xl border border-border/60 bg-white px-5 py-3.5 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        {/* Title block — replaces the old header bar + badge, folded inline on the left. */}
        <div className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-50 ring-1 ring-inset ring-teal-100">
            <CalendarRange className="h-4 w-4 text-teal-600" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h3 className="truncate text-sm font-semibold text-foreground">{title}</h3>
              <HelpTooltip
                content={`On the books — confirmed bookings for ${metrics.monthLabel} ${metrics.year}, including nights already stayed, not a forecast. Occupancy, revenue and ADR match the dashboard for this month.`}
                learnMore="/guides/occupancy-metrics"
              />
            </div>
            <p className="text-[11px] leading-tight text-muted-foreground">{subtitle}</p>
          </div>
        </div>

        {/* Metrics — flow to the right of the title, wrapping as needed. The left rule
            separates them from the title on wide screens and vanishes when stacked. */}
        <div className="flex flex-1 flex-wrap items-start gap-x-7 gap-y-3 sm:border-l sm:border-border/50 sm:pl-6">
          {metricTiles.map((tile) => (
            <Tile key={tile.label} metric={tile} />
          ))}
        </div>
      </div>
    </section>
  )
}
